import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw, LocateFixed, Plus, List, Map as MapIcon, ArrowUpRight, X, Radar, Thermometer, MapPin, Loader2 } from 'lucide-react';
import ExploreBar from '../components/ExploreBar';
import ExploreResults from '../components/ExploreResults';
import MaptoolkitMap from '../components/MaptoolkitMap';
import { areaRates, usePlaces, useRadar, useRates } from '../hooks/useBankData';
import { buildHeat, HEAT_COLORS, loadDongs } from '../lib/heat';
import { findRegion, useRegions } from '../hooks/useRegions';
import { matchesTier } from '../utils/sector';
import { bankKey, sameBank } from '../utils/bankKey';
import { pct } from '../utils/interest';

const GYEONGSAN = [35.8251, 128.7415];
const DEFAULT_SEL = { sido: '47', sgg: '47290', emd: '' }; // 경상북도 경산시
const SHORT = s => (s || '').replace(/^대구광역시/, '대구').replace(/^경상북도/, '경북');
const clampBounds = (center, b, max = 0.4) => {
  const h = Math.min(b[2] - b[0], max) / 2, w = Math.min(b[3] - b[1], max) / 2;
  return [center[0] - h, center[1] - w, center[0] + h, center[1] + w];
};

// 마커를 눌렀을 때 뜨는 카드: 그 은행의 상위 상품 3개
function PlaceCard({ place, kind, tier, term, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pick, setPick] = useState(0);
  useEffect(() => setPick(0), [place.id]);
  const data = useRates(place.isAtm ? { banks: ['__none__'] } : { banks: [place.bank || place.name], kind, tier, term, limit: 3, compact: true });
  const products = data.products;
  const chosen = products[pick] || products[0];
  return <div className="place-card" role="dialog" aria-label={place.name}>
    <div className="place-card-head">
      <span className={`avatar-ring ${place.product ? '' : 'muted'}`}><span className="avatar">{place.isAtm ? 'ATM' : (place.bank || place.name || '').replace(/은행|저축|\s/g, '').slice(0, 2)}</span></span>
      <div><strong>{place.name}</strong><small>{place.isAtm ? 'ATM' : t('map.branch')}{place.distanceKm != null && ` · ${place.distanceKm}km`}{place.address && ` · ${place.address}`}</small></div>
      <button className="icon-btn" aria-label={t('common.close')} onClick={onClose}><X size={18}/></button>
    </div>
    {place.isAtm ? <p className="place-note">{t('map.atmNote')}</p>
      : data.status === 'loading' && !products.length ? <p className="place-note">{t('common.loading')}</p>
      : !products.length ? <p className="place-note">{t('map.noMatch')}</p>
      : <>
        <ul className="place-products">
          {products.map((p, i) => <li key={p.id}><button className={i === pick ? 'on' : ''} onClick={() => setPick(i)} aria-pressed={i === pick}>
            <span className="pp-rank">{i + 1}</span>
            <span className="pp-name"><strong>{p.branchOnly && <span className="branch-badge">{t('radar.badge')}</span>}{p.name}</strong><small>{t('product.base')} {pct(p.base)} · {p.term}{t('common.months')}</small></span>
            <b>{pct(p.rate)}</b>
          </button></li>)}
        </ul>
        <button className="place-detail" onClick={() => navigate(`/product/${encodeURIComponent(chosen.id)}`)}>{t('map.detail')}<ArrowUpRight size={16}/></button>
      </>}
    {(place.source === 'kfcc' || place.source === 'nh') && place.sourceUrl && <a className="place-source" href={place.sourceUrl} target="_blank" rel="noreferrer">{t(place.source === 'kfcc' ? 'map.mgSource' : 'map.nhSource')}<ArrowUpRight size={13}/></a>}
  </div>;
}

const signed = v => (v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%p`);

// 창구 특판 레이더: 영업점에서만 가입되는 상품을 비대면 최고 금리와 비교해 보여줍니다.
function RadarPanel({ radar, items, onFocus, onClose }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const wins = items.filter(p => p.premium > 0).length;
  return <section className={`radar-panel ${open ? '' : 'is-folded'}`} aria-label={t('radar.title')}>
    <header>
      <span className="radar-icon"><Radar size={16}/></span>
      <button className="radar-title" onClick={() => setOpen(!open)} aria-expanded={open}>
        <strong>{t('radar.title')}</strong>
        <small>{radar.status === 'loading' && !items.length ? t('common.loading') : radar.onlineBest != null ? t('radar.onlineBest', { rate: pct(radar.onlineBest) }) : t('radar.sub')}</small>
      </button>
      {wins > 0 && <span className="radar-wins">{t('radar.wins', { count: wins })}</span>}
      <button className="icon-btn" aria-label={t('common.close')} onClick={onClose}><X size={16}/></button>
    </header>
    {open && <div className="radar-scroll">
      {radar.status === 'live' && !items.length && <p className="place-note">{t('radar.empty')}</p>}
      {radar.status === 'error' && <p className="place-note">{t('common.dataError')}</p>}
      {items.slice(0, 12).map(p => <button key={p.id} className={`radar-row ${p.premium > 0 ? 'is-win' : ''}`} onClick={() => onFocus(p)}>
        <span className="radar-row-text"><small>{p.bank}{p.branch ? ` · ${p.branch.distanceKm != null ? `${p.branch.distanceKm}km` : p.branch.name}` : ''}</small><strong>{p.name}</strong></span>
        <span className="radar-row-rate"><b>{pct(p.rate)}</b>{p.premium != null && <em>{t('radar.vsOnline')} {signed(p.premium)}</em>}</span>
      </button>)}
      <p className="radar-foot">{t('radar.note')}</p>
    </div>}
  </section>;
}

function HeatCard({ pick, onClose, onSearch }) {
  const { t } = useTranslation();
  return <div className="place-card heat-card" role="dialog" aria-label={pick.n}>
    <div className="place-card-head">
      <span className="heat-swatch" style={{ background: pick.color }}/>
      <div><strong>{pick.n}</strong><small>{pick.rate != null ? t('heat.cardSub', { count: pick.count }) : t('heat.noData')}</small></div>
      <button className="icon-btn" aria-label={t('common.close')} onClick={onClose}><X size={18}/></button>
    </div>
    {pick.rate != null && <div className="heat-card-body"><span>{t('heat.best')}</span><b>{pct(pick.rate)}</b><small>{pick.top}</small></div>}
    {onSearch && <button className="place-detail" onClick={onSearch}><MapPin size={16}/>{t('heat.searchDong', { name: pick.n })}</button>}
  </div>;
}

export default function MainPage() {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const regions = useRegions();
  const [sel, setSel] = useState(DEFAULT_SEL);
  const [area, setArea] = useState(null);
  const [viewport, setViewport] = useState(null);
  const baseline = useRef(null);
  const [moved, setMoved] = useState(false);
  // 상품 유형·금융권·기간은 다음 방문 때도 그대로 (브라우저에만 저장)
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem('bankpickle-filters')) || {}; } catch { return {}; } }, []);
  const [kind, setKind] = useState(saved.kind === 'saving' ? 'saving' : 'deposit');
  const [tier, setTier] = useState(['전체', '1금융', '저축은행', '신협', '새마을금고', '농협', 'ATM'].includes(saved.tier) ? saved.tier : '전체');
  const [term, setTerm] = useState([6, 12, 24, 36].includes(saved.term) ? saved.term : 12);
  useEffect(() => { try { localStorage.setItem('bankpickle-filters', JSON.stringify({ kind, tier, term })); } catch { /* 저장 불가 */ } }, [kind, tier, term]);
  const [selected, setSelected] = useState(null);
  const [focusPoint, setFocusPoint] = useState(null);
  const [toast, setToast] = useState('');
  const [rateOffset, setRateOffset] = useState(0);
  const listView = params.get('rates') === '1';
  const [radarOn, setRadarOn] = useState(false);
  const [heatOn, setHeatOn] = useState(false);
  const [heatPick, setHeatPick] = useState(null);
  const clearSelected = useCallback(() => { setSelected(null); setHeatPick(null); }, []);

  const go = useCallback(next => { if (!next) return; baseline.current = null; setMoved(false); setSelected(null); setRateOffset(0); setArea(next); }, []);
  useEffect(() => { if (regions && !area) go(findRegion(regions, DEFAULT_SEL)); }, [regions, area, go]);

  // ATM은 금융권 필터의 한 항목: 'ATM'이면 ATM만, '전체'면 지점과 ATM 모두
  const rateTier = tier === 'ATM' ? '전체' : tier;
  const showAtms = tier === '전체' || tier === 'ATM';
  const TIER_SECTOR = { '1금융': 'bank', 저축은행: 'savings', 신협: 'cu', 새마을금고: 'mg', 농협: 'nh' };
  const places = usePlaces(area, tier === 'ATM' || tier === '전체', TIER_SECTOR[tier] || null);
  const branchList = useMemo(() => places.items.filter(p => p.kind === 'bank'), [places.items]);
  const atmList = useMemo(() => places.items.filter(p => p.kind === 'atm'), [places.items]);
  const bankNames = useMemo(() => [...new Set(branchList.map(p => p.bank || p.name).filter(Boolean))].sort(), [branchList]);
  const banks = bankNames.length ? bankNames : ['__none__'];
  const rates = useRates({ kind, tier: rateTier, term, banks, limit: 30, offset: rateOffset, compact: true });
  const markerRates = useRates({ kind, tier: rateTier, term, banks, bestByBank: 1, limit: 100, offset: 0, compact: true });
  const productsByBank = useMemo(() => {
    const result = new Map();
    for (const p of markerRates.products) { const key = bankKey(p.bank); if (!result.has(key) || result.get(key).rate < p.rate) result.set(key, p); }
    return [...result];
  }, [markerRates.products]);
  const branches = useMemo(() => tier === 'ATM' ? [] : branchList.filter(b => matchesTier(b, tier)).map(b => {
    const key = bankKey(b.bank || b.name), nameKey = bankKey(b.name);
    const product = productsByBank.find(([k, p]) => sameBank(key, k, p.sector) || sameBank(nameKey, k, p.sector))?.[1];
    return { ...b, product };
  }), [branchList, tier, productsByBank]);

  // ── 창구 특판 레이더 ─────────────────────────────────────────
  const radar = useRadar({ banks: bankNames, kind, term, tier: rateTier, enabled: radarOn && tier !== 'ATM' });
  const radarBranches = useMemo(() => {
    if (!radarOn || !radar.products.length) return branches;
    const byBank = radar.products.map(p => [bankKey(p.bank), p]);
    return branches.map(b => {
      const key = bankKey(b.bank || b.name), nameKey = bankKey(b.name);
      const hit = byBank.find(([k, p]) => sameBank(key, k, p.sector) || sameBank(nameKey, k, p.sector))?.[1];
      return hit ? { ...b, radar: hit } : b;
    });
  }, [radarOn, radar.products, branches]);
  const attachBranch = useCallback(p => {
    const key = bankKey(p.bank);
    const branch = branchList.find(b => sameBank(bankKey(b.bank || b.name), key, p.sector) || sameBank(bankKey(b.name), key, p.sector));
    return branch ? { ...p, branch } : p;
  }, [branchList]);
  const radarItems = useMemo(() => radar.products.map(attachBranch), [radar.products, attachBranch]);

  const listed = useMemo(() => rates.products.map(attachBranch), [rates.products, attachBranch]);
  // Esc로 열린 카드 닫기
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !e.target.closest?.('input, select')) { setSelected(null); setHeatPick(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => { if (!toast) return; const id = setTimeout(() => setToast(''), 2600); return () => clearTimeout(id); }, [toast]);
  const focusProduct = p => {
    if (!p.branch) { setToast(t('map.noBranchToast', { bank: p.bank })); return; }
    setHeatPick(null);
    setSelected({ ...p.branch, isAtm: false, product: p });
    setFocusPoint({ key: `${p.id}:${p.branch.id}:${Date.now()}`, lat: p.branch.lat, lng: p.branch.lng });
    setParams({});
  };

  const onViewportChange = useCallback(next => {
    setViewport(next);
    if (!baseline.current || next.programmatic) { baseline.current = next; setMoved(false); return; }
    const b = baseline.current;
    const span = Math.max(b.bounds[2] - b.bounds[0], 0.005);
    setMoved(Math.hypot(next.center[0] - b.center[0], next.center[1] - b.center[1]) > span * 0.25 || Math.abs(next.zoom - b.zoom) > 0.7);
  }, []);
  const searchView = () => viewport && go({ key: `view:${viewport.center.map(n => n.toFixed(3)).join(',')}:${viewport.zoom.toFixed(1)}`, label: t('map.thisArea'), center: viewport.center, bounds: clampBounds(viewport.center, viewport.bounds), level: 'view', fromViewport: true });
  const searchRegion = () => { go(findRegion(regions, sel)); setParams({}); };
  const locate = () => {
    if (!navigator.geolocation) { setToast(t('map.locationUnavailable')); return; }
    navigator.geolocation.getCurrentPosition(p => {
      const c = [p.coords.latitude, p.coords.longitude];
      go({ key: `gps:${c.map(n => n.toFixed(3)).join(',')}`, label: t('map.currentLocation'), center: c, bounds: [c[0] - 0.02, c[1] - 0.025, c[0] + 0.02, c[1] + 0.025], level: 'gps' });
    }, () => setToast(t('map.locationError')), { timeout: 10000 });
  };

  // 지역 검색 후 첫 페이지를 불러오면 그 지점들이 화면에 꽉 차도록 한 번만 다시 맞춥니다.
  const [fit, setFit] = useState(null);
  useEffect(() => {
    if (!area || area.fromViewport || fit?.key === area.key || places.status !== 'live' || !places.items.length) return;
    const lats = places.items.map(p => p.lat), lngs = places.items.map(p => p.lng), pad = 0.004;
    setFit({ key: area.key, bounds: [Math.min(...lats) - pad, Math.min(...lngs) - pad, Math.max(...lats) + pad, Math.max(...lngs) + pad] });
  }, [area, places.status, places.items, fit?.key]);
  const focus = useMemo(() => {
    if (!area || area.fromViewport) return null;
    if (fit?.key === area.key) return { key: `${area.key}:items`, center: area.center, bounds: fit.bounds };
    return { key: area.key, center: area.center, bounds: area.bounds };
  }, [area, fit]);

  // ── 우리 동네 금리 온도 ──────────────────────────────────────
  // 화면에 걸친 시·군·구(최대 6곳, 화면 중심에서 가까운 순)의 읍·면·동을 색칠합니다.
  const heatSggs = useMemo(() => {
    if (!heatOn || !regions || !viewport) return [];
    if (viewport.zoom < 9) return null; // 너무 넓으면 안내만
    const [s, w, n, e] = viewport.bounds, [cy, cx] = viewport.center;
    return regions.sido.flatMap(sd => sd.g.map(g => ({ ...g, sido: sd.id })))
      .filter(g => g.b[0] <= n && g.b[2] >= s && g.b[1] <= e && g.b[3] >= w)
      .sort((a, b) => Math.hypot(a.c[0] - cy, a.c[1] - cx) - Math.hypot(b.c[0] - cy, b.c[1] - cx))
      .slice(0, 6);
  }, [heatOn, regions, viewport]);
  const heatKey = heatSggs ? `${heatSggs.map(g => g.id).sort().join(',')}|${kind}|${term}|${rateTier}` : '';
  const [heat, setHeat] = useState(null);
  useEffect(() => {
    if (!heatKey || !heatSggs?.length) { setHeat(null); return; }
    let active = true;
    setHeat(prev => (prev ? { ...prev, loading: true } : { loading: true }));
    Promise.all(heatSggs.map(g => Promise.all([loadDongs(g.id), areaRates(g.b, { kind, term, tier: rateTier }).catch(() => [])])))
      .then(parts => {
        if (!active) return;
        const seen = new Set(), pts = [];
        for (const [, list] of parts) for (const p of list) if (!seen.has(p.id)) { seen.add(p.id); pts.push(p); }
        setHeat({ ...buildHeat(parts.map(([fc]) => fc), pts), sggs: heatSggs.map(g => ({ id: g.id, sido: g.sido })) });
      })
      .catch(() => active && setHeat({ error: true }));
    return () => { active = false; };
  }, [heatKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const onHeatClick = useCallback(props => { setSelected(null); setHeatPick(props); }, []);
  const heatRegion = heatPick && heat?.sggs ? (() => {
    const sgg = heat.sggs.find(g => heatPick.id?.startsWith(g.id));
    const emd = sgg && regions?.sido.find(s => s.id === sgg.sido)?.g.find(g => g.id === sgg.id)?.e.find(e => e.id.slice(0, 8) === heatPick.id);
    return emd ? { sido: sgg.sido, sgg: sgg.id, emd: emd.id } : null;
  })() : null;

  const selLabel = SHORT(findRegion(regions, sel)?.label);
  const areaLabel = area?.fromViewport || area?.level === 'gps' ? area.label : SHORT(area?.label);

  return <main className={`map-page ${listView ? 'is-list' : ''}`}>
    <section className="map-stage" aria-label={t('nav.map')}>
      <ExploreBar regions={regions} regionLabel={selLabel} onPickRegion={item => setSel(item.sel)} kind={kind} setKind={v => { setKind(v); setRateOffset(0); }} tier={tier} setTier={v => { setTier(v); setRateOffset(0); setSelected(null); }} term={term} setTerm={v => { setTerm(v); setRateOffset(0); }} onSearch={searchRegion} canSearch={Boolean(sel.sgg)}/>
      <MaptoolkitMap center={GYEONGSAN} focus={focus} focusPoint={focusPoint} branches={radarBranches} atms={atmList} showAtms={showAtms && !radarOn} activeId={selected?.id} radarMode={radarOn && tier !== 'ATM'} heat={heatOn && heat?.geojson ? heat.geojson : null} onSelect={p => { setHeatPick(null); setSelected(p); }} onMapClick={clearSelected} onHeatClick={heatOn ? onHeatClick : null} onViewportChange={onViewportChange}/>
      {heatOn && !listView ? <div className={`heat-legend ${selected || heatPick ? 'is-hidden' : ''}`} aria-live="polite">
        <strong><Thermometer size={13}/>{t('heat.title')}</strong>
        {heatSggs === null ? <small>{t('heat.zoomIn')}</small>
          : heat?.error ? <small>{t('common.dataError')}</small>
          : heat?.count ? <>
            <div className="heat-scale">{HEAT_COLORS.map(c => <i key={c} style={{ background: c }}/>)}</div>
            <div className="heat-range"><span>{pct(heat.min)}</span><span>{pct(heat.max)}</span></div>
            {heat.hottest?.[0] && <small>{t('heat.hottest', { name: heat.hottest[0].name, rate: pct(heat.hottest[0].rate) })}</small>}
          </> : <small>{heat?.loading ? t('common.loading') : t('heat.noData')}</small>}
      </div>
      : <div className="map-legend" aria-hidden="true">{radarOn && <span><i className="lg-radar"/>{t('radar.legend')}</span>}<span><i className="lg-rate"/>{t('map.legendRate')}</span><span><i className="lg-plain"/>{t('map.legendNoRate')}</span><span><i className="lg-cluster"/>{t('map.legendCluster')}</span>{showAtms && !radarOn && <span><i className="lg-atm"/>ATM</span>}</div>}
      {moved && !listView && <button className="search-area" onClick={searchView}><RefreshCw size={15}/>{t('map.searchArea')}</button>}
      <span className="sr-only" aria-live="polite">{places.status === 'live' ? t('map.placesLoaded', { loaded: places.items.length, total: places.total }) : ''}</span>
      {area && !listView && places.status === 'live' && !places.total && <div className="map-hint">{t('map.noPlaces')}</div>}
      {area && !listView && places.status === 'error' && <div className="map-hint">{t('common.dataError')}</div>}
      {radarOn && !listView && !selected && !heatPick && tier !== 'ATM' && <RadarPanel radar={radar} items={radarItems} onFocus={focusProduct} onClose={() => setRadarOn(false)}/>}
      {heatPick && !listView && <HeatCard pick={heatPick} onClose={() => setHeatPick(null)} onSearch={heatRegion ? () => { setHeatPick(null); setSel(heatRegion); go(findRegion(regions, heatRegion)); } : null}/>}
      {selected && !listView && <PlaceCard place={selected} kind={kind} tier={rateTier} term={term} onClose={clearSelected}/>}
      {toast && <div className="toast" role="status">{toast}</div>}
      {listView && <div className="list-view"><ExploreResults products={listed} total={rates.total} offset={rateOffset} onMore={() => setRateOffset(rateOffset + 30)} status={rates.status} collectedAt={rates.collectedAt} term={term} regionLabel={areaLabel} activeId={selected?.product?.id} onFocus={focusProduct}/></div>}
      {!listView && <div className="map-tools">
        {places.hasMore && <button className={`more-btn ${places.status === 'loading' ? 'is-loading' : ''}`} onClick={places.loadMore} disabled={places.status === 'loading'} title={`${t('map.loadMore', { count: Math.min(20, places.total - places.items.length) })} · ${places.items.length}/${places.total}`} aria-label={t('map.loadMore', { count: Math.min(20, places.total - places.items.length) })}>
          {places.status === 'loading' ? <Loader2 size={19} className="spin"/> : <Plus size={20}/>}<i>{places.total - places.items.length}</i>
        </button>}
        <button className={`mode-btn radar ${radarOn ? 'active' : ''}`} aria-pressed={radarOn} title={t('radar.title')} onClick={() => { if (tier === 'ATM') setTier('전체'); setRadarOn(!radarOn); setSelected(null); }}><Radar size={19}/><span>{t('radar.short')}</span></button>
        <button className={`mode-btn heat ${heatOn ? 'active' : ''}`} aria-pressed={heatOn} title={t('heat.title')} onClick={() => { setHeatOn(!heatOn); setHeatPick(null); }}><Thermometer size={19}/><span>{t('heat.short')}</span></button>
        <button title={t('map.currentLocation')} aria-label={t('map.currentLocation')} onClick={locate}><LocateFixed size={20}/></button>
      </div>}
      <button className="view-toggle" onClick={() => setParams(listView ? {} : { rates: '1' })}>
        {listView ? <><MapIcon size={16}/>{t('map.showMap')}</> : <><List size={16}/>{t('map.showList')}{rates.total ? <b>{rates.total}</b> : null}</>}
      </button>
    </section>
  </main>;
}
